import { useUpdateOTLds } from '@phx-husky/use-update-ot-lds';
import { graphql } from 'gatsby';
import React, { FC, MouseEvent, useContext, useEffect } from 'react';

import ContactUsBlock from '~/components/ContactUsBlock';
import LdsBlock from '~/components/LdsBlock';
import { ExternalLinkDialogContext } from '~/context/ExternalLinkDialogContext';
import { Layout } from '~/layout/Layout';
import { contactUsBlockMapper } from '~/mappers/contactUsBlock';
import { ldsBlockMapper } from '~/mappers/ldsBlock';
import withContentstackPreview from '~/utils/livePreview';

import { ContentPageData } from './models';

export const LdsPage: FC<ContentPageData> = ({
  data: {
    contentstackLdsPage,
    contentstackHeader,
    contentstackL3HeaderExtension,
    contentstackFooter,
  },
  pageContext: { uid, locale, seoTitle, brand, fullPath },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const {
    seo,
    lds: [lds],
    pageHeader,
    fields,
    contactUs,
  } = contentstackLdsPage;
  const { displayModal } = useContext(ExternalLinkDialogContext);

  const { settings, businessId, fields: ldsFields } = lds;

  const { updatedLdsContent, isCheckingLdsVersion } = useUpdateOTLds({
    baseUrl: settings[0].ot?.baseUrl,
    businessId,
    versionId: ldsFields.ldsVersionId,
    platform: settings[0].platform,
    locale,
  });

  useEffect(() => {
    const linksArray = Array.from(
      document.querySelectorAll<HTMLAnchorElement>('.lds-block a')
    );

    linksArray.forEach((link) => {
      link.addEventListener('click', (e) =>
        displayModal(
          e as unknown as MouseEvent<HTMLAnchorElement>,
          link.getAttribute('href') as string
        )
      );
    });
  }, [displayModal]);

  const destructedContactUs =
    Array.isArray(contactUs) && contactUs.length > 0
      ? {
          ...contactUs[0],
          // To avoid duplication of entries with the same content in the CMS:
          pageBlock: { ...contactUs[0].pageBlock, mode: 'inverse' },
        }
      : undefined;

  return (
    <Layout
      {...{
        seo,
        header: { ...contentstackHeader, ...contentstackL3HeaderExtension },
        footer: contentstackFooter,
        className: 'lds-page',
        pageUid: uid,
        locale,
        brand,
        pageHeaderProps: { pageHeader, fields },
        fullPath,
      }}
    >
      {!isCheckingLdsVersion && (
        <LdsBlock {...ldsBlockMapper({ updatedLdsContent, ...lds })} />
      )}
      {destructedContactUs && (
        <ContactUsBlock {...contactUsBlockMapper(destructedContactUs)} />
      )}
    </Layout>
  );
};

export const query = graphql`
  query LdsPage($id: String, $locale: String, $brand: [String]) {
    contentstackLdsPage(id: { eq: $id }) {
      uid
      seo {
        ...SeoFragment
      }
      pageHeader: page_header {
        ...PageHeaderFragment
      }
      fields {
        ...PageFieldsFragment
      }
      contactUs: contact_us {
        ...ContactUsBlockFragment
      }
      lds {
        fields {
          ldsContent
          ldsVersionId
        }
        pageBlockUI: page_block_ui {
          ui {
            ...PageBlockUIFragment
          }
        }
        businessId: uri
        settings: lds_settings {
          ot {
            baseUrl: base_url
          }
          platform
        }
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderFragment
    }
    contentstackL3HeaderExtension(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...L3HeaderExtensionFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterFragment
    }
  }
`;

export default withContentstackPreview(LdsPage, 'lds_page');
